import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import { Badge } from '~/components/ui/badge';
import { CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { fetchSyncActivity } from '~/services/analytics';
import type { ISyncActivityEntry, SyncStatus } from '~/types';

const PROVIDER_LABELS: Record<string, string> = {
  hubspot: 'HubSpot',
  attio: 'Attio',
  slack: 'Slack',
};

function StatusBadgeCell({ status, errorMessage }: { status: SyncStatus; errorMessage: string | null }) {
  if (status === 'success') {
    return (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
        <CheckCircle2 className="mr-1 h-3 w-3" />
        Success
      </Badge>
    );
  }
  if (status === 'pending') {
    return (
      <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
        <Clock className="mr-1 h-3 w-3" />
        Pending
      </Badge>
    );
  }
  const badge = (
    <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
      <AlertCircle className="mr-1 h-3 w-3" />
      Failed
    </Badge>
  );
  if (errorMessage) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="text-xs">{errorMessage}</p>
        </TooltipContent>
      </Tooltip>
    );
  }
  return badge;
}

export const SyncActivityLog = ({ formId }: { formId: string }) => {
  const [entries, setEntries] = useState<ISyncActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSyncActivity(formId).then((data) => {
      setEntries(data);
      setLoading(false);
    });
  }, [formId]);

  return (
    <TooltipProvider>
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Sync Activity</h2>
        {loading ? (
          <div className="flex items-center justify-center h-24">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : entries.length === 0 ? (
          <div className="rounded-md border p-6 text-center text-sm text-muted-foreground">No sync activity yet</div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[160px]">Timestamp</TableHead>
                  <TableHead className="w-[200px]">Email</TableHead>
                  <TableHead className="w-[100px]">Provider</TableHead>
                  <TableHead className="w-[100px]">Sync Type</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(entry.createdAt), 'MMM d, yyyy HH:mm')}
                    </TableCell>
                    <TableCell className="text-sm">{entry.email || '--'}</TableCell>
                    <TableCell className="text-sm">{PROVIDER_LABELS[entry.provider] ?? entry.provider}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs capitalize">
                        {entry.syncType}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <StatusBadgeCell status={entry.status} errorMessage={entry.errorMessage} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[300px] truncate">
                      {entry.errorMessage ?? '--'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};
